import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { HeadlessListbox } from 'slab/components/HeadlessListbox'
import { Popper } from 'slab/components/Popper'

import { AutocompleteContext } from './context'
import { AutocompleteInput } from './Input'
import { AutocompleteList } from './List'
import { AutocompleteOption } from './Option'

export interface AutocompleteProps {
  children: React.ReactNode;
  /**
   * Callback when the open state changes. Useful for delaying API calls until
   * the user interacts with the autocomplete input.
   */
  onOpenChange?: (open: boolean) => void;
}

// React overrides the setter for `value` so we need to use
// `Object.getOwnPropertyDescriptor` to get the original setter
function setValue (input: HTMLInputElement, value: string) {
  const setValue = Object?.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )?.set

  setValue?.call(input, value)
}

export function Autocomplete ({
  children,
  onOpenChange
}: AutocompleteProps) {
  const [open, _setOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return

    const handleInteractOutside = (event: MouseEvent | FocusEvent) => {
      // Prevent closing when clicking the input's label. Focus will move right
      // back to the input and cause an unsightly flash of the dropdown.
      const label = document.querySelector(
        'label[for="' + inputRef.current?.id + '"]'
      )

      // Prevent closing when clicking inside Slab components like `<TextInput />`
      // that render other interactive elements within the field container.
      const formField = document.querySelector(
        '[data-form-field-for="' + inputRef.current?.id + '"]'
      )

      const list = document.getElementById(listId)

      const clickedInLabel = label?.contains(event.target as Node)
      const clickedInFormField = formField?.contains(event.target as Node)
      const clickedInList = list?.contains(event.target as Node)

      if (!(clickedInLabel || clickedInFormField || clickedInList)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleInteractOutside)
    document.addEventListener('focusin', handleInteractOutside)

    return () => {
      document.removeEventListener('mousedown', handleInteractOutside)
      document.removeEventListener('focusin', handleInteractOutside)
    }
  }, [open])

  const setOpen = useCallback(
    (open: boolean) => {
      _setOpen(open)
      onOpenChange?.(open)
    },
    [onOpenChange]
  )

  const onSelect = useCallback((selectedValue: string | null, { closeAfterSelection = true } = {}) => {
    if (inputRef.current && selectedValue !== null) {
      setValue(inputRef.current, selectedValue)
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (closeAfterSelection) {
      setOpen(false)
    }
  }, [])

  const contextValue = useMemo(() => ({
    inputRef,
    listId,
    open,
    onSelect,
    setOpen
  }), [listId, onSelect, open])

  return (
    <Popper>
      <HeadlessListbox>
        <AutocompleteContext.Provider value={contextValue}>
          {children}
        </AutocompleteContext.Provider>
      </HeadlessListbox>
    </Popper>
  )
}

Autocomplete.Input = AutocompleteInput
Autocomplete.List = AutocompleteList
Autocomplete.Option = AutocompleteOption
